import { useMemo, useState } from 'react'
import type { UIEvent } from 'react'
import type { NewsItem } from '../../api/client'
import { Bewertung, bewertungsTon } from './Bewertung'
import { nurUhr, zeitKurz, zeitTag, zeitUhr } from './newsZeit'
import s from './NewsListe.module.scss'

/** Eine Gruppe der Sicht „nach Betrifft": alle Meldungen zu EINEM ersten Ziel. */
export interface NewsGruppe {
  name: string
  eintraege: NewsItem[]
  /** Der Schnitt der Bewertungen — nur als Hinweis, siehe die Notiz der Seite. */
  schnitt: number
}

/** Was in der Zeile hinter „Betrifft" steht: die Namen, dann die Anzahl Verknüpfungen. */
function betrifftKurz(item: NewsItem): string {
  const namen = item.targets.map((ziel) => ziel.name ?? ziel.ref)
  return namen.length ? namen.join(' · ') : 'ohne Bezug'
}

/** In der Gruppe steht das erste Ziel schon über der Gruppe — also nur die weiteren. */
function betrifftAuch(item: NewsItem): string {
  const weitere = item.targets.slice(1).map((ziel) => ziel.name ?? ziel.ref)
  return weitere.length ? `auch ${weitere.join(' · ')}` : ''
}

/** Nach dem ersten Ziel gruppiert, in der Reihenfolge des ersten Auftretens. */
export function gruppiere(items: NewsItem[]): NewsGruppe[] {
  const gruppen = new Map<string, NewsItem[]>()
  for (const item of items) {
    const schluessel = item.targets[0]?.name ?? item.targets[0]?.ref ?? 'ohne Bezug'
    const liste = gruppen.get(schluessel) ?? []
    liste.push(item)
    gruppen.set(schluessel, liste)
  }
  return [...gruppen.entries()].map(([name, eintraege]) => ({
    name, eintraege,
    schnitt: eintraege.reduce((summe, e) => summe + e.sentiment, 0) / eintraege.length,
  }))
}

const schnittFormat = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 1, maximumFractionDigits: 1, signDisplay: 'exceptZero',
})

/**
 * Die Meldungsliste — der linke Teil der Master-Detail-Ansicht.
 *
 * Kompakte Zeilen: Bewertung, Titel, Betrifft, Zeit. Die gewählte Zeile trägt
 * einen Strich links und eine helle Fläche. Nach Betrifft gruppiert stehen die
 * Meldungen unter dem ersten Ziel mit Anzahl und Schnitt, jede Gruppe
 * zuklappbar. Die Liste scrollt in sich; am Ende ruft sie nach.
 */
export function NewsListe({ items, gewaehlt, onWaehle, gruppiert, onEndReached,
                            loadingMore, leer, fuss }: {
  items: NewsItem[]
  gewaehlt: number | null
  onWaehle: (id: number) => void
  gruppiert: boolean
  onEndReached?: () => void
  loadingMore?: boolean
  /** Was bei leerer Liste steht. */
  leer: string
  /** Die Zeile unter der Liste, etwa „30 Meldungen". */
  fuss?: string
}) {
  const [zu, setZu] = useState<Set<string>>(new Set())
  const gruppen = useMemo(() => gruppiere(items), [items])

  function onScroll(event: UIEvent<HTMLDivElement>) {
    const el = event.currentTarget
    if (onEndReached && el.scrollTop + el.clientHeight >= el.scrollHeight - 120) onEndReached()
  }

  const zeile = (item: NewsItem, inGruppe: boolean) => (
    <Zeile key={item.id} item={item} aktiv={item.id === gewaehlt}
           inGruppe={inGruppe} onClick={() => onWaehle(item.id)} />
  )

  return (
    <div className={s.liste}>
      <div className={s.rollen} onScroll={onScroll}>
        {items.length === 0 && <div className={s.leer}>{leer}</div>}

        {gruppiert
          ? gruppen.map((gruppe) => {
              const offen = !zu.has(gruppe.name)
              return (
                <div key={gruppe.name}>
                  <button type="button" className={s.gruppe}
                          aria-expanded={offen}
                          onClick={() => setZu((jetzt) => {
                            const naechste = new Set(jetzt)
                            if (naechste.has(gruppe.name)) naechste.delete(gruppe.name)
                            else naechste.add(gruppe.name)
                            return naechste
                          })}>
                    <span className={s.pfeil} aria-hidden="true">{offen ? '▾' : '▸'}</span>
                    <span className={s.gruppeName}>{gruppe.name}</span>
                    <span className={s.gruppeAnzahl}>{gruppe.eintraege.length}</span>
                    <span className={s.gruppeSchnitt} data-ton={bewertungsTon(Math.round(gruppe.schnitt))}
                          title="Schnitt der Bewertungen — nur ein Hinweis: +3 und −3 ergeben 0">
                      Ø {schnittFormat.format(gruppe.schnitt)}
                    </span>
                  </button>
                  {offen && gruppe.eintraege.map((item) => zeile(item, true))}
                </div>
              )
            })
          : items.map((item) => zeile(item, false))}

        {loadingMore && <div className={s.nachladen}>lädt weitere Meldungen …</div>}
      </div>
      {fuss && (
        <div className={s.fuss}>
          <span>{fuss}</span>
          <span title="Pfeiltasten wechseln die Meldung">↑↓ wechseln</span>
        </div>
      )}
    </div>
  )
}

function Zeile({ item, aktiv, inGruppe, onClick }: {
  item: NewsItem
  aktiv: boolean
  inGruppe: boolean
  onClick: () => void
}) {
  const [zeitOffen, setZeitOffen] = useState(false)
  const nebenzeile = inGruppe ? betrifftAuch(item) : `Betrifft ${betrifftKurz(item)}`
  return (
    <button type="button" className={s.zeile} data-aktiv={aktiv || undefined}
            data-eingerueckt={inGruppe || undefined} onClick={onClick}
            aria-current={aktiv ? 'true' : undefined}>
      <Bewertung wert={item.sentiment} />
      <span className={s.text}>
        <span className={s.titel}>{item.headline}</span>
        {nebenzeile && <span className={s.betrifft}>{nebenzeile}</span>}
      </span>
      {/*
        Die kurze Zeit trägt beim Überfahren die lange: Tag, Uhrzeit mit
        Weltzeit, und die beiden Zeitpunkte, die sonst auseinanderlaufen —
        wann die Meldung erschien und wann wir sie erfasst haben.
      */}
      <span className={s.zeit}
            onMouseEnter={() => setZeitOffen(true)}
            onMouseLeave={() => setZeitOffen(false)}>
        <span className={s.zeitKurz}>{zeitKurz(item.published_at)}</span>
        {zeitOffen && (
          <span className={s.zeitTip} role="tooltip">
            <b>{zeitTag(item.published_at)}</b>
            <span className={s.zeitUhr}>{zeitUhr(item.published_at)}</span>
            <span className={s.zeitFuss}>
              Veröffentlicht {nurUhr(item.published_at)} · erfasst {nurUhr(item.created)}
            </span>
          </span>
        )}
      </span>
    </button>
  )
}
